import { FC } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { Locale } from '@bts-web/utils-lokalise';
import {
  PrecisionFloat,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import {
  BelowInputInfoTable,
  Collapsible,
  getAssetDisplayValueFromSource,
} from '../../../common';
import { RegularTaxInfo } from '../../../common/svgIcons';

const taxBoxPrimaryContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'extra_small',
  alignItems: 'start',
  padding: 'small',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'warning.stroke_primary',
  borderRadius: 8,
  textAlign: 'start',
});

export type TradeTaxSummaryTranslations = {
  taxDetails: string;
  taxWitheld: string;
  youReceive: string;
  cryptoTaxInfoText1: string;
  cryptoTaxInfoText2: string;
  taxSystemOfflineWarningTitle: string;
  taxSystemOfflineWarningSubtitle: string;
};

type TradeTaxSummaryProps = {
  currency: string;
  isTaxServiceOffline?: boolean;
  translations: TradeTaxSummaryTranslations;
  currentLocale: Locale;
  taxAmount: PrecisionFloat;
  receivedAmount: PrecisionFloat;
  receivedAmountDisplayType?: 'currency' | 'units';
  symbol?: string;
};

const getAmount = ({
  amount,
  displayType,
  symbol,
  currency,
  locale,
}: {
  amount: NonNullable<PrecisionFloat>;
  displayType: 'currency' | 'units';
  symbol?: string;
  currency: string;
  locale: Locale;
}) => {
  if (displayType === 'units' && !symbol) {
    throw new Error(
      '[getAmount function error]: symbol argument is required for units display type',
    );
  }

  return displayType === 'currency'
    ? intlFiatValueFormatting(amount.value as string, {
        locale,
        notation: 'standard',
        currency,
        fractionDigits: amount.precision,
      })
    : getAssetDisplayValueFromSource({
        sourceType: 'ASSET',
        sourceAmount: amount.value,
        customSymbol: symbol,
        locale,
      });
};

export const TradeTaxSummary: FC<TradeTaxSummaryProps> = ({
  currency,
  currentLocale,
  receivedAmount,
  isTaxServiceOffline,
  taxAmount,
  translations,
  receivedAmountDisplayType = 'currency',
  symbol,
}) => {
  return (
    <Collapsible
      header={{
        LeftContentIndicatorSlot: (
          <span className={css({ display: 'flex', gap: 'extra_small_3' })}>
            <span
              className={css({
                ...(isTaxServiceOffline
                  ? {
                      color: 'warning.text_primary',
                    }
                  : {}),
              })}
            >
              {translations.taxDetails}
            </span>

            {isTaxServiceOffline && (
              <RegularTaxInfo
                size={18}
                date-element="offline-tax-icon"
                data-testid="offline-tax-icon"
                color="warning.text_primary"
              />
            )}
          </span>
        ),
      }}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'medium',
        })}
      >
        <BelowInputInfoTable
          variant={isTaxServiceOffline ? 'warning' : 'primary'}
          items={[
            {
              title: translations.taxWitheld,
              value: getAmount({
                amount: taxAmount as NonNullable<PrecisionFloat>,
                displayType: 'currency',
                locale: currentLocale,
                currency,
              }),
            },
            {
              title: translations.youReceive,
              value: getAmount({
                amount: receivedAmount as NonNullable<PrecisionFloat>,
                displayType: receivedAmountDisplayType,
                symbol,
                locale: currentLocale,
                currency,
              }),
            },
          ]}
        />

        {isTaxServiceOffline && (
          <div className={taxBoxPrimaryContainerStyles}>
            <p
              className={css({
                color: 'warning.text_primary',
                fontSize: 'label.medium_medium',
                fontWeight: 'label.medium_medium',
                lineHeight: 'label.medium_medium',
                letterSpacing: 'label.medium_medium',
              })}
            >
              {translations.taxSystemOfflineWarningTitle}
            </p>
            <p
              className={css({
                color: 'neutrals.text_primary',
                fontSize: 'body.medium',
                fontWeight: 'body.medium',
                lineHeight: 'body.medium',
                letterSpacing: 'body.medium',
              })}
            >
              {translations.taxSystemOfflineWarningSubtitle}
            </p>
          </div>
        )}

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small',
            color: 'neutrals.text_secondary',
            fontSize: 'caption.medium_medium',
            fontWeight: 'caption.medium_medium',
            letterSpacing: 'caption.medium_medium',
            lineHeight: 'caption.medium_medium',
          })}
        >
          <p>{translations.cryptoTaxInfoText1}</p>
          <p>{translations.cryptoTaxInfoText2}</p>
        </div>
      </div>
    </Collapsible>
  );
};
